import { useEffect, useState } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';
import { dashboardApi, type BayDetail, type BayJob } from '@/api/dashboardApi';
import { workOrdersApi } from '@/api/workOrdersApi';
import Toast from '@/components/ui/Toast';

interface BayUtilizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BayUtilizationModal({ isOpen, onClose }: BayUtilizationModalProps) {
  const [bays, setBays] = useState<BayDetail[]>([]);
  const [selectedBay, setSelectedBay] = useState<BayDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [totalBays, setTotalBays] = useState(0);
  const [occupiedCount, setOccupiedCount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      loadBayUtilization();
    }
  }, [isOpen]);

  async function loadBayUtilization() {
    setLoading(true);
    try {
      const response = await dashboardApi.getBayUtilization();
      console.log('Bay utilization response:', response);
      setBays(response.bays || []);
      setTotalBays(response.totalBays);
      setOccupiedCount(response.occupiedBays);
      if (response.bays?.length > 0) {
        const occupied = response.bays.find((b) => b.status === 'Occupied');
        setSelectedBay(occupied || response.bays[0]);
      } else {
        console.warn('No bays in response');
      }
    } catch (error) {
      console.error('Failed to load bay utilization:', error);
      setToastMessage('Failed to load bay data');
    } finally {
      setLoading(false);
    }
  }

  async function handleCompleteAndClear(bayId: number) {
    const bay = bays.find((b) => b.bayId === bayId);
    if (!bay?.currentJob) {
      setToastMessage('No job in this bay');
      return;
    }

    setCompleting(true);
    try {
      await workOrdersApi.completeFromBay(bay.currentJob.workOrderId);
      setToastMessage('Job completed and bay cleared');
      await loadBayUtilization();
    } catch (error) {
      console.error('Failed to complete job:', error);
      setToastMessage('Failed to complete job');
    } finally {
      setCompleting(false);
    }
  }

  if (!isOpen) return null;

  const formatTimeElapsed = (createdDate: string) => {
    const created = new Date(createdDate);
    const now = new Date();
    const hours = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60));
    const minutes = Math.floor(((now.getTime() - created.getTime()) % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h2 className="text-xl font-bold">Service Bays</h2>
            <p className="text-sm text-slate-600 mt-1">{occupiedCount}/{totalBays} bays occupied</p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin w-6 h-6 border-3 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Bay Grid */}
              <div>
                <h3 className="font-semibold text-sm mb-4">Bay Status</h3>
                <div className="grid grid-cols-5 gap-3">
                  {bays.map((bay) => (
                    <button
                      key={bay.bayId}
                      onClick={() => setSelectedBay(bay)}
                      className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        selectedBay?.bayId === bay.bayId
                          ? 'border-primary bg-primary/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <p className="font-bold text-lg mb-2">Bay {bay.bayNumber}</p>
                      <div
                        className={`h-12 rounded flex items-center justify-center text-xs font-semibold ${
                          bay.status === 'Occupied'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {bay.status === 'Occupied' ? '■ OCCUPIED' : '○ FREE'}
                      </div>
                      {bay.currentJob && (
                        <p className="text-xs text-slate-600 mt-2 truncate">{bay.currentJob.workOrderNumber}</p>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Bay Details */}
              {selectedBay && (
                <div className="border border-slate-200 rounded-lg p-6 bg-slate-50">
                  <h3 className="font-bold text-lg mb-4">Bay {selectedBay.bayNumber} — {selectedBay.status}</h3>

                  {selectedBay.currentJob ? (
                    <div className="space-y-4">
                      {/* Job Info */}
                      <div className="grid grid-cols-2 gap-4 bg-white p-4 rounded-lg border border-slate-200">
                        <div>
                          <p className="text-xs text-slate-600 font-medium">WORK ORDER</p>
                          <p className="text-lg font-bold">#{selectedBay.currentJob.workOrderNumber}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 font-medium">VEHICLE</p>
                          <p className="text-sm font-semibold">{selectedBay.currentJob.vehicle}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 font-medium">CUSTOMER</p>
                          <p className="text-sm">{selectedBay.currentJob.customerName}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 font-medium">TECHNICIAN</p>
                          <p className="text-sm">{selectedBay.currentJob.assignedTechName}</p>
                        </div>
                        <div className="col-span-2">
                          <p className="text-xs text-slate-600 font-medium">SERVICE</p>
                          <p className="text-sm line-clamp-2">{selectedBay.currentJob.serviceDescription}</p>
                        </div>
                      </div>

                      {/* Status & Time */}
                      <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-slate-200">
                        <div>
                          <p className="text-xs text-slate-600 font-medium">STATUS</p>
                          <p className="text-sm font-semibold text-blue-600">{selectedBay.currentJob.status}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-600 font-medium">TIME IN BAY</p>
                          <p className="text-sm font-semibold">{formatTimeElapsed(selectedBay.currentJob.createdDate)}</p>
                        </div>
                        <button
                          onClick={() => handleCompleteAndClear(selectedBay.bayId)}
                          disabled={completing}
                          className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          {completing ? 'Completing...' : 'Complete & Clear'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">This bay is free and ready for the next job</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {toastMessage && (
        <Toast
          message={toastMessage}
          onClose={() => setToastMessage(null)}
          type="success"
          duration={3000}
        />
      )}
    </div>
  );
}
